import React, { useState } from 'react';
import { Modal, ModalBody, Spinner } from 'reactstrap';
import { IPicture } from '../models/CheckList';
import Icon from './Icon';
import Button from './Button';

type Props = {
  pictures: IPicture[];
  loading?: boolean;
};

const CheckListPhotoGallery = ({ pictures, loading }: Props) => {
  const [selectedImage, setSelectedImage] = useState<IPicture | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const openModal = (picture: IPicture, index: number) => {
    setSelectedImage(picture);
    setCurrentIndex(index);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : pictures.length - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(pictures[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex < pictures.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
    setSelectedImage(pictures[newIndex]);
  };

  if (loading) {
    return (
      <div className="text-center p-3">
        <Spinner color="primary">Loading...</Spinner>
      </div>
    );
  }

  if (!pictures || pictures.length === 0) {
    return (
      <div className="text-center p-3">
        <Icon icon="image" local width={40} className="text-secondary mb-2" />
        <p className="small text-secondary mb-0">No photos attached</p>
      </div>
    );
  }

  const isVideo = (picture: IPicture) => {
    if (picture.content_type) {
      return picture.content_type.startsWith('video/');
    }
    // Fallback to extension check if content_type is missing (for old records)
    const extension = picture.file.split('.').pop()?.toLowerCase();
    return ['mp4', 'mov', 'webm', 'ogg'].includes(extension || '');
  };

  return (
    <>
      <div className="d-flex flex-wrap gap-2">
        {pictures.map((picture, index) => (
          <div
            key={picture.id}
            className="position-relative"
            style={{ cursor: 'pointer' }}
            onClick={() => openModal(picture, index)}
          >
            {isVideo(picture) ? (
              <div
                className="rounded border d-flex align-items-center justify-content-center bg-dark"
                style={{
                  width: '120px',
                  height: '120px',
                  backgroundSize: 'contain',
                  backgroundImage: `url(${picture.thumbnail || picture.file})`
                }}
              >
                <Icon icon="play" width={40} color="white" />
              </div>
            ) : (
              <img
                src={picture.thumbnail || picture.file}
                alt={`Checklist item ${index + 1}`}
                className="rounded border"
                style={{
                  width: '120px',
                  height: '120px',
                  objectFit: 'contain'
                }}
              />
            )}
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded"
              style={{
                backgroundColor: 'rgba(0,0,0,0)',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0)';
              }}
            >
              <Icon
                icon={isVideo(picture) ? 'play' : 'search'}
                local
                width={24}
                className="text-white"
              />
            </div>
          </div>
        ))}
      </div>

      {selectedImage && (
        <Modal
          isOpen={!!selectedImage}
          toggle={closeModal}
          size="xl"
          centered
          className="photo-viewer-modal"
        >
          <ModalBody className="p-0 bg-dark position-relative">
            <div
              className="d-flex justify-content-between align-items-center p-3 position-absolute w-100 top-0 start-0"
              style={{ zIndex: 10 }}
            >
              <span className="text-white fw-bold">
                {isVideo(selectedImage) ? 'Video' : 'Photo'} {currentIndex + 1}{' '}
                of {pictures.length}
              </span>
              <Button
                color="light"
                className="text-dark"
                onClick={closeModal}
                size="sm"
              >
                <Icon local icon="close" width={20} />
              </Button>
            </div>

            <div
              className="d-flex align-items-center justify-content-center"
              style={{ minHeight: '70vh' }}
            >
              {isVideo(selectedImage) ? (
                <div className="d-flex flex-column align-items-center">
                  <video
                    key={selectedImage.file}
                    src={selectedImage.file}
                    controls
                    autoPlay
                    playsInline
                    className="img-fluid"
                    style={{
                      maxHeight: '70vh',
                      maxWidth: '100%',
                      objectFit: 'contain'
                    }}
                  />
                  <a
                    href={selectedImage.file}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-light btn-sm mt-3"
                  >
                    Open video in new tab
                  </a>
                </div>
              ) : (
                <img
                  src={selectedImage.file}
                  alt={`Item ${currentIndex + 1}`}
                  className="img-fluid"
                  style={{
                    maxHeight: '80vh',
                    maxWidth: '100%',
                    objectFit: 'contain'
                  }}
                />
              )}
            </div>

            {pictures.length > 1 && (
              <>
                <Button
                  color="light"
                  className="position-absolute top-50 start-0 ms-3"
                  onClick={goToPrevious}
                  style={{ transform: 'translateY(-50%)' }}
                >
                  <Icon icon="arrow-left" local width={24} />
                </Button>
                <Button
                  color="light"
                  className="position-absolute top-50 end-0 me-3"
                  onClick={goToNext}
                  style={{ transform: 'translateY(-50%)' }}
                >
                  <Icon icon="arrow-right" local width={24} />
                </Button>
              </>
            )}
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default CheckListPhotoGallery;
